import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { Formik, Form, Field } from "formik";
import { Button, Container, FormControlLabel, IconButton, makeStyles, Paper, Switch, TextField, Tooltip } from "@material-ui/core";
import { DeleteOutline, LocationOnOutlined, LocationOffOutlined } from "@material-ui/icons";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";

import DefaultFunctionsUtils from "../../utils/classes/DefaultFunctionsUtils";
import DefaultValuesUtils from "../../utils/classes/DefaultValuesUtils";

import { useGlobalRegisters } from "../../context/GlobalRegisters/GlobalRegistersContext";

const useStyles = makeStyles(theme => ({
	paper: {
		padding: theme.spacing(2),
		display: "flex",
		alignItems: "center",
		marginBottom: 12,
	},

  paperLogoImage: {
    padding: theme.spacing(2),
		alignItems: "center",
		marginBottom: 12,
  },

  hiddenField: {
    display: "none",
  },

  logoImage: {
    width: "300px",
    height: "300px",
    cursor: "pointer",
  },

  locationContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "1em",
  },

  locationFieldsContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-around",
    gap: "5px",
  },

  locationButtonsContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
    gap: "5px",
  },

  floatingButton: {
    transition: "transform 0.3s",
    "&:hover": { transform: "translateY(-5px)", },
  },

  actionButton: {
    "&:hover": { color: theme.palette.primary.main, },
  },
}));

const MyProfile = () => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const initialState = useMemo(() => ({
    name: ""
    , logoFile: ""
    , sendLogoAsSticker: false
    , latitude: ""
    , longitude: ""
    , accuracy: ""
  }), []);

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [myProfile, setMyProfile] = useState(initialState);

  const { setShouldSyncRouteOnMenu } = useGlobalRegisters();

  

  //  ****************
  //  ** Use Effect **
  //  **************** 
  useEffect(() => {
    if (user.profile === "user" || !user.configEnabled) {
      DefaultFunctionsUtils.redirectToTicketsPage(history);
      setShouldSyncRouteOnMenu(true);
    }

		const fetchSession = async () => {
			try {
				const { data: myProfile } = await api.get("/myProfile");
				setMyProfile(myProfile);

        const { logoUrl } = myProfile;

        if (logoUrl) {
          const { data } = await api.get(logoUrl, { responseType: "blob", });

          const filename = logoUrl.split(`/public/${user.tenantId}/`).pop();
          const file = new File([data], filename, { type: data.type });
          if (file) setSelectedLogo(file);
        }
			}
      catch (exception) {
        console.log("MyProfile Use Effect Exception:", exception);
        toastError(exception);
      }
		};

		fetchSession();

    // eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);



  //  ***************
  //  ** CallBacks **
  //  ***************
  const successLocationCallback = (position) => {
    setMyProfile({ 
      ...myProfile
      , latitude: position.coords.latitude
      , longitude: position.coords.longitude
      , accuracy: position.coords.accuracy
    });
  };

  const errorLocationCallback = (error) => {
    //  Error Codes:
    //
    //   - 1: (PERMISSION_DENIED) - The acquisition of the geolocation information failed because the page did not
    // have the permission to do it.
    //   - 2: (POSITION_UNAVAILABLE) - The acquisition of the geolocation failed because one or several internal
    // sources of position returned an internal error.
    //   - 3: (TIMEOUT) - Geolocation information was not obtained in the allowed time.
    //
    if (error.code === 1) {
      toast.info(i18n.t("location.errors.permissionNotGranted"));
      return;
    }
    
    if (error.code === 2) {
      toast.info(i18n.t("location.errors.positionUnavaible"));
      return;
    }
    
    toast.error(i18n.t("location.errors.timeout"));
  };
  


  //  **************
  //  ** Function **
  //  **************
  const handleUpdateLogoInfoDOM = (data) => {
    const { logoValue, logoInputValue, logoImageSource, logoImageDisplay, shouldRemoveSource } = data;

    if (logoValue !== null && logoValue !== undefined) {
      const profileLogo = document.getElementById("profileLogo");
      if (profileLogo) profileLogo.value = logoValue;
    }

    if (logoInputValue !== null && logoInputValue !== undefined) {
      const profileLogoInput = document.getElementById("profileLogoInput");
      if (profileLogoInput) profileLogoInput.value = logoInputValue;
    }

    if (logoImageSource !== null && logoImageSource !== undefined) {
      const inputLogoImage = document.getElementById("inputLogoImage");
      if (inputLogoImage) inputLogoImage.src = logoImageSource;
      if (shouldRemoveSource) inputLogoImage.removeAttribute("src");
    }

    if (logoImageDisplay !== null && logoImageDisplay !== undefined) {
      const paperLogoImage = document.getElementById("paperLogoImage");
      if (paperLogoImage) paperLogoImage.style.display = logoImageDisplay;
    }
  };

  const handleCompanyName = (event) => {
    setMyProfile({ ...myProfile, name: event.target.value });
  };

  const handleRemoveLogoClick = async () => {
    handleUpdateLogoInfoDOM({
      logoValue: ""
      , logoInputValue: ""
      , logoImageSource: ""
      , shouldRemoveSource: true
    });

    try {
      await api.put("/myProfile/removeLogo");
      toast.success(i18n.t("profile.success"));
    }
    catch (exception) {
      console.log("handleRemoveLogoClick Profile Exception:", exception);
      toastError(exception);
    }
  };

  const handleChangeLogoClick = () => {
    const profileLogoInput = document.getElementById("profileLogoInput");
    if (profileLogoInput) profileLogoInput.click();
  };

  const handleChangeLogo = (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    if (!DefaultValuesUtils.ALLOWED_COMPANY_LOGO_MIMETYPES.includes(uploadedFile.type)) {     
      handleUpdateLogoInfoDOM({ logoValue: "", logoInputValue: "", logoImageSource: "" });
      toast.info(i18n.t("profile.toasts.nonSupportedLogo"));
      return;
    }

    setSelectedLogo(uploadedFile);

    handleUpdateLogoInfoDOM({
      logoValue: uploadedFile.name
      , logoImageSource: URL.createObjectURL(uploadedFile)
      , logoImageDisplay: "block"
    });
  };

  const handleUpdateProfile = async () => {
    const formData = new FormData();
    formData.append("name", myProfile.name);
    formData.append("sendLogoAsSticker", myProfile.sendLogoAsSticker);
    formData.append("latitude", myProfile.latitude);
    formData.append("longitude", myProfile.longitude);
    formData.append("accuracy", myProfile.accuracy);
    if (selectedLogo) formData.append("logoFile", selectedLogo);

    try {
      const { data } = await api.put("/myProfile", formData);
      if (data.success) toast.success(i18n.t("profile.success"));
      else toast.info(i18n.t(`profile.toasts.${data.message}`));
      
      handleUpdateLogoInfoDOM({ logoInputValue: "" });
    }
    catch (exception) {
      console.log("Handle Update Profile Exception:", exception);
      toastError(exception);
    }
  };

  const handleOpenConfirmationModal = () => {
    setConfirmModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
  };

  const handleTurnOnLocation = () => {
    navigator.geolocation.getCurrentPosition(successLocationCallback, errorLocationCallback);
  };

  const handleTurnOffLocation = () => {
    setMyProfile({ ...myProfile, latitude: "-", longitude: "-", accuracy: "-" });
  };

  const handleChangeSendLogoAsSticker = () => {
    setMyProfile({ ...myProfile, sendLogoAsSticker: !myProfile.sendLogoAsSticker });
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("profile.title")}</Title>

        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => await handleUpdateProfile()}
            className={classes.floatingButton}
          >
            {i18n.t("profile.buttons.save")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Container>
        <Formik initialValues={myProfile} enableReinitialize={true}>
          <Form>
            <Paper className={classes.paper}>
              <Field
                as={TextField}
                label={i18n.t("profile.labels.name")}
                autoFocus
                autoComplete="off"
                name="name"
                variant="outlined"
                margin="dense"
                fullWidth
                onChange={(event) => handleCompanyName(event)}
              />
            </Paper>
            


            {/* 
              ***********
              ** Input **
              ***********
            */}
            <div hidden="hidden">
              <Paper className={classes.paper}>
                <Field
                  as={TextField}
                  id="profileLogo"
                  helperText={i18n.t("profile.labels.logo")}
                  margin="dense"
                  variant="outlined"
                  fullWidth
                  placeholder={i18n.t("profile.labels.logoField")}
                  name="logoField"
                  InputProps={{ readOnly: true, }}
                  onClick={handleChangeLogoClick}
                />

                <input
                  name="logoFile"
                  className={classes.hiddenField}
                  margin="dense"
                  variant="outlined"
                  id="profileLogoInput"
                  type="file"
                  accept={DefaultValuesUtils.ALLOWED_COMPANY_LOGO_MIMETYPES.join(", ")}
                  onChange={(event) => handleChangeLogo(event)}
                />
              </Paper>
            </div>


            {/*
              ******************
              ** Logo Preview **
              ******************
            */}
            <Paper id="paperLogoImage" className={classes.paperLogoImage}>
              <center>
                <div onClick={handleChangeLogoClick}>
                  {i18n.t("profile.labels.logoField")}  
                </div>

                <img 
                  id="inputLogoImage"
                  alt=""
                  className={classes.logoImage}
                  src={myProfile.logoUrl}
                  onClick={handleChangeLogoClick}
                />

                <br />

                {/*
                  ************************** 
                  ** Send Logo as Sticker **
                  **************************
                */}
                <div>
                  <FormControlLabel
                    control={<Field as={Switch} onClick={handleChangeSendLogoAsSticker} color="primary" name="isBot" checked={myProfile.sendLogoAsSticker} />}
                    label={i18n.t("profile.labels.sendLogoAsSticker")}
                  />
                </div>

                <br />

                <ConfirmationModal
                  title={`${i18n.t("myProfile.confirmationModal.deleteTitle")}?`}
                  open={confirmModalOpen}
                  onClose={handleCloseConfirmationModal}
                  onConfirm={async () => await handleRemoveLogoClick()}
                >
                  {i18n.t("myProfile.confirmationModal.deleteMessage")}
                </ConfirmationModal>

                <IconButton
                  size="small"
                  variant="contained"
                  color="inherit"
                  onClick={handleOpenConfirmationModal}
                >
                  <DeleteOutline className={`${classes.actionButton} ${classes.floatingButton}`} />
                </IconButton>
              </center>
            </Paper>

            
            {/* 
              **************
              ** Location **
              **************
            */}
            <Paper className={classes.paper}>
              <div className={classes.locationContainer}>
                <div className={classes.locationFieldsContainer}>
                  <Field
                    as={TextField}
                    label={i18n.t("profile.labels.latitude")}
                    name="latitude"
                    variant="outlined"
                    margin="dense"
                  />

                  <Field
                    as={TextField}
                    label={i18n.t("profile.labels.longitude")}
                    name="longitude"
                    variant="outlined"
                    margin="dense"
                  />

                  <Field
                    as={TextField}
                    label={i18n.t("profile.labels.accuracy")}
                    name="accuracy"
                    variant="outlined"
                    margin="dense"
                  />
                </div>

                <div className={classes.locationButtonsContainer}>
                  <Tooltip title={i18n.t("profile.tooltips.turnOnGeolocation")} placement="top-start" arrow>
                    <IconButton
                      size="small"
                      variant="contained"
                      color="inherit"
                      onClick={handleTurnOnLocation}
                    >
                      <LocationOnOutlined className={`${classes.actionButton} ${classes.floatingButton}`} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={i18n.t("profile.tooltips.turnOffGeolocation")} placement="top-start" arrow>
                    <IconButton
                      size="small"
                      variant="contained"
                      color="inherit"
                      onClick={handleTurnOffLocation}
                    >
                      <LocationOffOutlined className={`${classes.actionButton} ${classes.floatingButton}`} />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            </Paper>
          </Form>
        </Formik>
      </Container>
    </MainContainer>
  );
};

export default MyProfile;
